import { ComponentProps, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router'

import { useSetDocumentTitle } from '@/common/hooks'
import { BackButton } from '@/components/componentLibrary'
import { HeaderBar } from '@/components/curb/HeaderBar'
import { PolicyDetailsTemplate } from '@/components/layouts'
import { MobilityThemeSwitch } from '@/components/mobility/theme/MobilityThemeSwitch'
import { useMobilityPoliciesAccess } from '@/modules/permissions/pageAccessHooks'
import { EditHeader } from '@/modules/policiesV1/policyLibrary/components/headers/EditHeader'
import { Form } from '@/modules/policiesV1/policyLibrary/components/PolicyDetails/Form'
import { PolicyDetailsMap } from '@/modules/policiesV1/policyLibrary/components/PolicyDetailsMap'
import { PolicyInfoModal } from '@/modules/policiesV1/policyLibrary/components/PolicyInfoModal'
import { ThemedPolicyInfoModal } from '@/modules/policiesV1/policyLibrary/components/PolicyInfoModal/ThemedPolicyInfoModal'
import { PolicyNotFound } from '@/modules/policiesV1/policyLibrary/components/PolicyNotFound'
import { ThemedPolicyEditView } from '@/modules/policiesV1/policyLibrary/components/ThemedPolicyDetails/ThemedPolicyEditView'
import {
  PolicyEditContext,
  usePolicyEditController,
} from '@/modules/policiesV1/policyLibrary/policyEditController'
import { getModalText } from '@/modules/policiesV1/policyLibrary/utils/modalText'
import AccessGatekeeper from '@/modules/urlRouting/AccessGatekeeper'
import { useNavigateOnRegionChange } from '@/modules/urlRouting/hooks'
import { PATHS, TO_PARENT_PATH } from '@/modules/urlRouting/paths'

import { EditState } from './constants'

const PolicyDetailsEditPage: FC<{ editState: EditState }> = props => {
  const hasAccess = useMobilityPoliciesAccess()
  return (
    <AccessGatekeeper hasAccess={hasAccess}>
      <PolicyDetailsEditPageContent {...props} />
    </AccessGatekeeper>
  )
}

const PolicyDetailsEditPageContent: FC<{ editState: EditState }> = ({ editState }) => {
  useSetDocumentTitle('Mobility Manager - Policies')
  useNavigateOnRegionChange({ to: PATHS.MOBILITY.POLICIES._PATH, replace: true })

  const { t } = useTranslation()
  const navigate = useNavigate()
  const controller = usePolicyEditController({ editState })

  if (controller.policyNotFound) return <PolicyNotFound />
  if (controller.savedPolicyId)
    return (
      <Navigate
        to={`${TO_PARENT_PATH}/${
          controller.isNew || controller.isDuplicate ? controller.savedPolicyId : ''
        }`}
        state={{ policyJson: controller.originalPolicy?.toJson }}
      />
    )

  const { policy } = controller

  const templateProps: ComponentProps<typeof PolicyDetailsTemplate> = {
    disabled: controller.isFetching || controller.isSaving,
    backLinkContent: (
      <BackButton
        onClick={() =>
          controller.setModalProps({
            ...getModalText().INFO_CANCEL,
            onCancel: controller.clearModalProps,
            onConfirm: () => {
              controller.clearModalProps()
              navigate(controller.isNew ? TO_PARENT_PATH : `${TO_PARENT_PATH}/${TO_PARENT_PATH}`)
            },
          })
        }
        text={t('policiesLibrary.backToPolicies', 'Back To Policies')}
      />
    ),
    headerContent: <EditHeader />,
    mapContent: <PolicyDetailsMap shapeLayerUUID={policy?.shapeLayerUUID || undefined} />,
    detailsContent: policy && (
      <Form
        editable
        editPolicy={controller.editPolicy}
        policy={policy}
        errors={controller.formErrors}
        clearErrors={controller.clearFormErrors}
        setErrors={controller.setFormErrors}
      />
    ),
  }

  return (
    <PolicyEditContext value={controller}>
      {/* Each UI has its own tree, so the old one keeps the order it had before the flag. */}
      <MobilityThemeSwitch
        legacy={
          <>
            <HeaderBar title={t('urlRouting.drawerItemPoliciesLibrary', 'Policies Library')} />
            {controller.modalProps && <PolicyInfoModal {...controller.modalProps} />}
            <PolicyDetailsTemplate {...templateProps} />
          </>
        }
        themed={
          <>
            {controller.modalProps && <ThemedPolicyInfoModal {...controller.modalProps} />}
            <ThemedPolicyEditView />
          </>
        }
      />
    </PolicyEditContext>
  )
}

export default PolicyDetailsEditPage
